/**
 * UI-facing cache/state for CreativeElements ("elements").
 *
 * Holds: the category list, a per-category lazy cache of elements for the panel,
 * and a fetch cache (+ in-flight de-dupe) used by the template load hook so the
 * same component fetches once across ads/instances.
 *
 * SCAFFOLD: state + action stubs. Bodies filled in phase by phase.
 */
import { acceptHMRUpdate, defineStore } from 'pinia';
import type {
  CreativeElement,
  CreativeElementCategory,
  CreativeElementTagResponse,
  VersionPrompt
} from '@/types/creativeElement';
import type {
  CreativeElementState,
  DetachChoice,
  ElementsBucket,
  VersionChoice
} from '@/types/stores/creativeElement';
import creativeElementService from '@/services/api/creativeElementService';
import { useAppStore } from '@/stores/app';
import {
  CURATED_BUILT_IN_KEYS,
  RETIRED_BUILT_IN_KEYS
} from '@/pages/Editor/constants/elements';

function toCategory(tag: CreativeElementTagResponse): CreativeElementCategory {
  return {
    id: tag.id,
    name: tag.name,
    thumbnailUrl: tag.thumbnailUrl,
    isDefault: tag.isDefault
  };
}

/**
 * In-flight de-dupe (module-level: promises don't belong in reactive state).
 * Shares one request per componentId, so several instances of the same
 * component resolve through a single fetch. Failed entries are dropped so a
 * later attempt retries instead of failing forever.
 */
const inFlightFetches = new Map<number, Promise<CreativeElement>>();

/**
 * The category fetch currently in flight, so a second caller awaits it instead
 * of racing ahead against an empty list.
 */
let inFlightCategories: Promise<void> | null = null;

// Resolvers for the open prompts.
let detachPromptResolver: ((choice: DetachChoice) => void) | null = null;
let versionPromptResolver: ((choice: VersionChoice) => void) | null = null;

/** Page size for the Elements panel's paginated category lists. */
export const ELEMENTS_PAGE_SIZE = 12;

/** Page size for the paginated category (tag) list itself. */
export const CATEGORIES_PAGE_SIZE = 20;

function createElementsBucket(): ElementsBucket {
  return {
    items: [],
    totalCount: 0,
    pageNumber: 0,
    loading: false,
    initialized: false,
    searchQuery: ''
  };
}

const EMPTY_BUCKET: ElementsBucket = Object.freeze(createElementsBucket());

/** Drop retired built-ins, then the curated ones the profile may not see. */
function filterHiddenOut(
  categories: CreativeElementCategory[]
): CreativeElementCategory[] {
  const visible = categories.filter(
    category =>
      !(
        category.isDefault &&
        RETIRED_BUILT_IN_KEYS.has(category.name.trim().toLowerCase())
      )
  );
  if (!useAppStore().isCuratedLibraryHidden) return visible;
  return visible.filter(
    category =>
      !(
        category.isDefault &&
        CURATED_BUILT_IN_KEYS.has(category.name.trim().toLowerCase())
      )
  );
}

export const useCreativeElementStore = defineStore('creativeElement', {
  state: (): CreativeElementState => ({
    categories: [],
    categoriesLoaded: false,
    categoriesLoading: false,
    categoriesTotalCount: 0,
    categoriesPageNumber: 0,
    categorySearchQuery: '',
    categorySearchResults: [],
    categorySearchLoading: false,
    elementsByTag: {},
    cache: {},
    detachPromptOpen: false,
    detachPromptNames: [],
    versionPrompt: null
  }),

  getters: {
    /** Whether a page of categories is still waiting on the server. */
    hasMoreCategories: (state): boolean =>
      state.categories.length < state.categoriesTotalCount,

    /**
     * Categories the current profile may see. A profile with
     * `hideCuratedLibrary` set loses the seeded curated ones — matched by
     * `isDefault` AND name, so a category the user created for themselves
     * survives even when it shares a built-in's name.
     *
     * Everything that lists categories reads through here: the Elements panel
     * grid and the save-as-element dropdown, so neither can drift from the
     * other or become a way around the flag.
     */
    visibleCategories(state): CreativeElementCategory[] {
      return filterHiddenOut(state.categories);
    },

    /** `visibleCategories` for the server-side search results. */
    visibleCategorySearchResults(state): CreativeElementCategory[] {
      return filterHiddenOut(state.categorySearchResults);
    },

    elementsBucket:
      state =>
      (tagId: number): ElementsBucket =>
        state.elementsByTag[tagId] ?? EMPTY_BUCKET
  },

  actions: {
    resetForBusinessProfile(): void {
      if (this.detachPromptOpen) this.resolveDetachConfirm('cancel');
      if (this.versionPrompt) this.resolveVersionChoice('keep');

      inFlightFetches.clear();
      inFlightCategories = null;

      this.$reset();
    },

    /** Loads the first page of categories once, de-duping concurrent callers. */
    async loadCategories(): Promise<void> {
      if (this.categoriesLoaded) return;
      if (inFlightCategories) return inFlightCategories;
      const bpId = useAppStore().businessProfileId;
      if (bpId == null) return;
      inFlightCategories = this.fetchCategories(true).finally(() => {
        inFlightCategories = null;
      });
      return inFlightCategories;
    },

    // Runs a name search against the server.
    async searchCategories(query: string): Promise<void> {
      const trimmed = query.trim();
      this.categorySearchQuery = trimmed;

      if (trimmed.length === 0) {
        this.categorySearchResults = [];
        this.categorySearchLoading = false;
        return;
      }

      const bpId = useAppStore().businessProfileId;
      if (bpId == null) return;

      this.categorySearchLoading = true;
      try {
        const tags = await creativeElementService.searchCategories(
          bpId,
          trimmed
        );

        // A slower earlier request must not overwrite a newer query's results.
        if (this.categorySearchQuery !== trimmed) return;
        this.categorySearchResults = tags.map(toCategory);
      } finally {
        if (this.categorySearchQuery === trimmed) {
          this.categorySearchLoading = false;
        }
      }
    },

    /** Appends the next page of categories, if the server still has one. */
    async loadMoreCategories(): Promise<void> {
      if (this.categoriesLoading || !this.hasMoreCategories) return;
      return this.fetchCategories();
    },

    /**
     * Fetches one page of categories. `reset` restarts from page 1 and replaces
     * the list; otherwise the next page is appended.
     *
     * Order is the server's: the built-in categories come back first and only
     * on page one, then the user categories most recently updated first. Pages
     * are appended as they arrive, so nothing is re-sorted here.
     */
    async fetchCategories(reset = false): Promise<void> {
      const bpId = useAppStore().businessProfileId;
      if (bpId == null || this.categoriesLoading) return;

      const pageNumber = reset ? 1 : this.categoriesPageNumber + 1;
      this.categoriesLoading = true;
      try {
        const { items, totalCount } =
          await creativeElementService.listCategories(bpId, {
            pageNumber,
            pageSize: CATEGORIES_PAGE_SIZE
          });
        const fetched = items.map(toCategory);
        if (reset) {
          this.categories = fetched;
        } else {
          const existing = new Set(this.categories.map(c => c.id));
          this.categories = [
            ...this.categories,
            ...fetched.filter(c => !existing.has(c.id))
          ];
        }
        this.categoriesTotalCount = totalCount;
        this.categoriesPageNumber = pageNumber;
        this.categoriesLoaded = true;
      } finally {
        this.categoriesLoading = false;
      }
    },

    /** Creates a user tag and appends it to the loaded list. */
    async createCategory(
      name: string,
      imageFile: string
    ): Promise<CreativeElementCategory | null> {
      const bpId = useAppStore().businessProfileId;
      if (bpId == null) return null;

      /**
       * The first page must be in before the new category is spliced in, or a
       * fetch still on the wire would replace the list and drop it. Callers fire
       * `loadCategories` when their form opens, so this normally resolves
       * instantly; it only actually waits when the user beat the fetch.
       */
      await this.loadCategories();

      const tag = await creativeElementService.createCategory(bpId, {
        name,
        imageFile
      });
      const category = toCategory(tag);
      const firstUserIndex = this.categories.findIndex(c => !c.isDefault);
      this.categories.splice(
        firstUserIndex === -1 ? this.categories.length : firstUserIndex,
        0,
        category
      );
      this.categoriesTotalCount += 1;
      return category;
    },

    /**
     * Returns (creating if missing) the panel bucket for one tag.
     */
    getElementsBucket(tagId: number): ElementsBucket {
      if (!this.elementsByTag[tagId]) {
        this.elementsByTag[tagId] = createElementsBucket();
      }
      return this.elementsByTag[tagId]!;
    },

    /**
     * Loads one page of a tag's elements for the panel. `reset` restarts from
     * page 1 and replaces the list; otherwise the next page is appended
     */
    async loadElementsForCategory(tagId: number, reset = false): Promise<void> {
      const bucket = this.getElementsBucket(tagId);
      const bpId = useAppStore().businessProfileId;
      if (bpId == null) return;
      // A new search must not be dropped just because a page is in flight; the
      // older call bails on the query check below.
      if (bucket.loading && !reset) return;

      const query = bucket.searchQuery;
      const pageNumber = reset
        ? 1
        : Math.floor(bucket.items.length / ELEMENTS_PAGE_SIZE) + 1;
      bucket.loading = true;
      try {
        const { items, totalCount } =
          await creativeElementService.listComponents(bpId, {
            tag: tagId,
            pageNumber,
            pageSize: ELEMENTS_PAGE_SIZE,
            search: query
          });

        // A newer query landed mid-flight; that call owns the bucket now.
        if (bucket.searchQuery !== query) return;

        if (reset) {
          bucket.items = items;
        } else {
          const existing = new Set(bucket.items.map(i => i.id));
          bucket.items = [
            ...bucket.items,
            ...items.filter(i => !existing.has(i.id))
          ];
        }
        bucket.totalCount = totalCount;
        bucket.pageNumber = pageNumber;
        bucket.initialized = true;
      } finally {
        // an outdated one must not clear the newer call's spinner.
        if (bucket.searchQuery === query) bucket.loading = false;
      }
    },

    setCategorySearch(tagId: number, query: string): void {
      const bucket = this.getElementsBucket(tagId);
      const trimmed = query.trim();
      if (bucket.searchQuery === trimmed && bucket.initialized) return;

      bucket.searchQuery = trimmed;
      void this.loadElementsForCategory(tagId, true);
    },

    /**
     * Files a just-saved component at the top of its tag's list so the panel
     * updates without a refetch (the save response already carries the row).
     */
    upsertElement(tagId: number, element: CreativeElement): void {
      const bucket = this.getElementsBucket(tagId);
      const index = bucket.items.findIndex(item => item.id === element.id);
      if (index === -1) {
        bucket.items = [element, ...bucket.items];
        bucket.totalCount += 1;
      } else {
        bucket.items[index] = element;
      }
      if (Object.keys(element.layerData.refTree).length > 0) {
        this.cache[element.id] = element;
      }
    },

    /**
     * Renames a component. The tile updates immediately and reverts if the
     * request fails.
     */
    async renameElement(
      tagId: number,
      elementId: number,
      name: string
    ): Promise<boolean> {
      const trimmed = name.trim();
      const bpId = useAppStore().businessProfileId;
      if (bpId == null || trimmed.length === 0) return false;

      const bucket = this.getElementsBucket(tagId);
      const index = bucket.items.findIndex(item => item.id === elementId);
      const previousName = index === -1 ? null : bucket.items[index]!.name;
      if (index !== -1) bucket.items[index]!.name = trimmed;

      try {
        await creativeElementService.renameComponent(bpId, elementId, trimmed);
      } catch {
        if (index !== -1 && previousName !== null) {
          bucket.items[index]!.name = previousName;
        }
        return false;
      }

      const cached = this.cache[elementId];
      if (cached) cached.name = trimmed;
      return true;
    },

    /**
     * Soft-deletes a component and drops it from its tag's panel list.
     */
    async deleteElement(tagId: number, elementId: number): Promise<boolean> {
      const bpId = useAppStore().businessProfileId;
      if (bpId == null) return false;

      try {
        await creativeElementService.deleteComponent(bpId, elementId);
      } catch {
        return false;
      }

      const bucket = this.getElementsBucket(tagId);
      const remaining = bucket.items.filter(item => item.id !== elementId);
      if (remaining.length !== bucket.items.length) {
        bucket.items = remaining;
        bucket.totalCount = Math.max(0, bucket.totalCount - 1);
      }
      delete this.cache[elementId];
      if (bucket.items.length === 0 && bucket.totalCount > 0) {
        void this.loadElementsForCategory(tagId, true);
      }
      return true;
    },

    /**
     * Returns a component by id, fetching once and de-duping concurrent
     * calls — parallel per-ad tree builds share a single request.
     */
    async ensureComponent(componentId: number): Promise<CreativeElement> {
      const cached = this.cache[componentId];
      if (cached) return cached;

      const bpId = useAppStore().businessProfileId;
      if (bpId == null) {
        throw new Error('businessProfileId is not available yet');
      }

      let pending = inFlightFetches.get(componentId);
      if (!pending) {
        pending = creativeElementService
          .getComponent(bpId, componentId)
          .then(component => {
            this.cache[componentId] = component;
            return component;
          })
          .finally(() => inFlightFetches.delete(componentId));
        inFlightFetches.set(componentId, pending);
      }
      return pending;
    },

    /**
     * Detach-on-edit prompt
     * -----------------------------------------------------------------
     * Await-bridge between the detach guard (plain service code, which can't
     * mount anything) and the ConfirmDialog rendered at the Editor root. The
     * guard calls `requestDetachConfirm` and awaits; the dialog resolves it.
     */
    requestDetachConfirm(names: string[] = []): Promise<DetachChoice> {
      // A prompt is already open (shouldn't happen — the guard serializes via
      // its session) — settle the stale one so nothing hangs forever.
      detachPromptResolver?.('cancel');

      return new Promise<DetachChoice>(resolve => {
        detachPromptResolver = resolve;
        this.detachPromptNames = names;
        this.detachPromptOpen = true;
      });
    },

    /** Called by the mounted dialog on confirm / cancel / dismiss. */
    resolveDetachConfirm(choice: DetachChoice): void {
      detachPromptResolver?.(choice);
      detachPromptResolver = null;
      this.detachPromptOpen = false;
      this.detachPromptNames = [];
    },

    /**
     * Component-version prompt
     * -----------------------------------------------------------------
     * Same await-bridge shape as the detach prompt: the reconcile service is
     * plain code and cannot mount anything, so it asks here and awaits while
     * the dialog rendered at the Editor root resolves.
     *
     * The reconcile loop asks once per outdated component and awaits each
     * answer before moving to the next, so only one prompt is ever live.
     */
    requestVersionChoice(prompt: VersionPrompt): Promise<VersionChoice> {
      // Shouldn't happen (the loop serializes), but never leave a caller
      // hanging on a prompt that got replaced.
      versionPromptResolver?.('keep');

      return new Promise<VersionChoice>(resolve => {
        versionPromptResolver = resolve;
        this.versionPrompt = prompt;
      });
    },

    /** Called by the mounted dialog on choose / dismiss. */
    resolveVersionChoice(choice: VersionChoice): void {
      versionPromptResolver?.(choice);
      versionPromptResolver = null;
      this.versionPrompt = null;
    }
  }
});

// Keep this store self-accepting so editing it (or files in its chain) does a
// hot update instead of a full page reload during dev.
// Remove this after development is complete and the store is stable.
if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHMRUpdate(useCreativeElementStore, import.meta.hot)
  );
}
